import { useState } from "react";
import { formatDistanceToNow } from "date-fns";
import { sv } from "date-fns/locale";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Skeleton } from "@/components/ui/skeleton";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { CheckCircle2, XCircle, Clock, MapPin, Loader2, ExternalLink, Lightbulb } from "lucide-react";
import { AdminLayout } from "@/components/admin/AdminLayout";
import { 
  useAllPlaceSuggestions, 
  useApprovePlaceSuggestion, 
  useRejectPlaceSuggestion,
  type PlaceSuggestion
} from "@/hooks/usePlaceSuggestions";
import { Link } from "react-router-dom";

export default function AdminPlaceSuggestionsPage() {
  const [activeTab, setActiveTab] = useState<'pending' | 'approved' | 'rejected'>('pending');
  const [approveDialogOpen, setApproveDialogOpen] = useState(false);
  const [rejectDialogOpen, setRejectDialogOpen] = useState(false);
  const [selectedSuggestion, setSelectedSuggestion] = useState<PlaceSuggestion & { producers: { id: string; name: string; slug: string } | null } | null>(null);
  const [rejectNote, setRejectNote] = useState("");
  const [typeFilter, setTypeFilter] = useState<string | null>(null);

  const { data: suggestions, isLoading } = useAllPlaceSuggestions(activeTab);
  const approveMutation = useApprovePlaceSuggestion();
  const rejectMutation = useRejectPlaceSuggestion();

  const pendingCount = suggestions?.filter(s => s.status === 'pending').length ?? 0;

  const handleApprove = () => {
    if (selectedSuggestion) {
      approveMutation.mutate(selectedSuggestion.id);
      setApproveDialogOpen(false);
      setSelectedSuggestion(null);
    }
  };

  const handleReject = () => {
    if (selectedSuggestion) {
      rejectMutation.mutate({ 
        suggestionId: selectedSuggestion.id, 
        adminNote: rejectNote || undefined 
      });
      setRejectDialogOpen(false);
      setSelectedSuggestion(null);
      setRejectNote("");
    }
  };

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'pending':
        return <Badge variant="secondary"><Clock className="h-3 w-3 mr-1" />Väntande</Badge>;
      case 'approved':
        return <Badge variant="default"><CheckCircle2 className="h-3 w-3 mr-1" />Godkänd</Badge>;
      case 'rejected':
        return <Badge variant="destructive"><XCircle className="h-3 w-3 mr-1" />Avvisad</Badge>;
      default:
        return <Badge variant="outline">{status}</Badge>;
    }
  };

  return (
    <AdminLayout title="Platsförslag & Värdtips">
      <Tabs value={activeTab} onValueChange={(v) => setActiveTab(v as 'pending' | 'approved' | 'rejected')}>
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-4">
          <TabsList>
            <TabsTrigger value="pending" className="relative">
              Väntande
              {pendingCount > 0 && (
                <span className="ml-2 h-5 w-5 rounded-full bg-primary text-primary-foreground text-xs flex items-center justify-center">
                  {pendingCount}
                </span>
              )}
            </TabsTrigger>
            <TabsTrigger value="approved">Godkända</TabsTrigger>
            <TabsTrigger value="rejected">Avvisade</TabsTrigger>
          </TabsList>
          <div className="flex gap-2">
            <Button
              size="sm"
              variant={typeFilter === null ? 'default' : 'outline'}
              onClick={() => setTypeFilter(null)}
            >
              Alla
            </Button>
            <Button
              size="sm"
              variant={typeFilter === 'place' ? 'default' : 'outline'}
              onClick={() => setTypeFilter('place')}
            >
              <MapPin className="h-3 w-3 mr-1" />
              Platser
            </Button>
            <Button
              size="sm"
              variant={typeFilter === 'host' ? 'default' : 'outline'}
              onClick={() => setTypeFilter('host')}
            >
              <Lightbulb className="h-3 w-3 mr-1" />
              Värdtips
            </Button>
          </div>
        </div>

        <TabsContent value={activeTab} className="mt-4">
          {(() => {
            const filtered = typeFilter
              ? suggestions?.filter((s: any) => s.suggestion_type === typeFilter)
              : suggestions;

            if (isLoading) return (
              <div className="space-y-2">
                <Skeleton className="h-12 w-full" />
                <Skeleton className="h-12 w-full" />
                <Skeleton className="h-12 w-full" />
              </div>
            );

            if (!filtered?.length) return (
              <div className="text-center py-12 border rounded-lg bg-muted/20">
                <MapPin className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
                <h3 className="text-lg font-medium">Inga förslag</h3>
                <p className="text-muted-foreground mt-1">
                  {activeTab === 'pending' 
                    ? "Det finns inga väntande förslag just nu."
                    : `Inga ${activeTab === 'approved' ? 'godkända' : 'avvisade'} förslag.`}
                </p>
              </div>
            );

            return (
            <div className="border rounded-lg">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Föreslagen plats</TableHead>
                    <TableHead>Ort</TableHead>
                    <TableHead>Värd</TableHead>
                    <TableHead>Skapat</TableHead>
                    <TableHead>Status</TableHead>
                    {activeTab === 'pending' && <TableHead className="text-right">Åtgärder</TableHead>}
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filtered?.map((suggestion: any) => (
                    <TableRow key={suggestion.id}>
                      <TableCell>
                        <div>
                          <div className="flex items-center gap-1.5">
                            {suggestion.suggestion_type === 'host' && (
                              <Lightbulb className="h-3.5 w-3.5 text-amber-500 shrink-0" />
                            )}
                            <p className="font-medium">{suggestion.suggested_name}</p>
                          </div>
                          {suggestion.note && (
                            <p className="text-xs text-muted-foreground mt-0.5 line-clamp-1">{suggestion.note}</p>
                          )}
                          {suggestion.contact_info && (
                            <p className="text-xs text-primary mt-0.5">{suggestion.contact_info}</p>
                          )}
                          {suggestion.address_line1 && (
                            <p className="text-xs text-muted-foreground">{suggestion.address_line1}</p>
                          )}
                        </div>
                      </TableCell>
                      <TableCell>
                        {suggestion.city || "-"}
                        {suggestion.region && <span className="text-muted-foreground">, {suggestion.region}</span>}
                      </TableCell>
                      <TableCell>
                        {suggestion.producers ? (
                          <Link 
                            to={`/app/admin/producers/${suggestion.producers.id}`}
                            className="text-primary hover:underline inline-flex items-center gap-1"
                          >
                            {suggestion.producers.name}
                            <ExternalLink className="h-3 w-3" />
                          </Link>
                        ) : (
                          <span className="text-muted-foreground">-</span>
                        )}
                      </TableCell>
                      <TableCell className="text-muted-foreground text-sm">
                        {formatDistanceToNow(new Date(suggestion.created_at), { 
                          addSuffix: true, 
                          locale: sv 
                        })}
                      </TableCell>
                      <TableCell>{getStatusBadge(suggestion.status)}</TableCell>
                      {activeTab === 'pending' && (
                        <TableCell className="text-right">
                          <div className="flex gap-2 justify-end">
                            <Button
                              size="sm"
                              onClick={() => {
                                setSelectedSuggestion(suggestion);
                                setApproveDialogOpen(true);
                              }}
                              disabled={approveMutation.isPending}
                            >
                              {approveMutation.isPending ? (
                                <Loader2 className="h-4 w-4 animate-spin" />
                              ) : (
                                <>
                                  <CheckCircle2 className="h-4 w-4 mr-1" />
                                  Godkänn
                                </>
                              )}
                            </Button>
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => {
                                setSelectedSuggestion(suggestion);
                                setRejectDialogOpen(true);
                              }}
                              disabled={rejectMutation.isPending}
                            >
                              <XCircle className="h-4 w-4 mr-1" />
                              Avvisa
                            </Button>
                          </div>
                        </TableCell>
                      )}
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
            );
          })()}
        </TabsContent>
      </Tabs>

      {/* Approve dialog */}
      <AlertDialog open={approveDialogOpen} onOpenChange={setApproveDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Godkänn platsförslag?</AlertDialogTitle>
            <AlertDialogDescription>
              Detta kommer att skapa en ny plats "{selectedSuggestion?.suggested_name}" och automatiskt 
              koppla den till värden "{selectedSuggestion?.producers?.name}".
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Avbryt</AlertDialogCancel>
            <AlertDialogAction onClick={handleApprove}>
              Godkänn och skapa plats
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Reject dialog */}
      <AlertDialog open={rejectDialogOpen} onOpenChange={setRejectDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Avvisa platsförslag?</AlertDialogTitle>
            <AlertDialogDescription>
              Förslaget "{selectedSuggestion?.suggested_name}" kommer att markeras som avvisat.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <div className="py-4">
            <Label htmlFor="reject-note">Notering (valfritt)</Label>
            <Textarea
              id="reject-note"
              placeholder="Förklara varför förslaget avvisas..."
              value={rejectNote}
              onChange={(e) => setRejectNote(e.target.value)}
              className="mt-2"
            />
          </div>
          <AlertDialogFooter>
            <AlertDialogCancel>Avbryt</AlertDialogCancel>
            <AlertDialogAction onClick={handleReject} className="bg-destructive text-destructive-foreground hover:bg-destructive/90">
              Avvisa
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </AdminLayout>
  );
}
